import React, { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

type Contributions = {
  bands: number
  people: number
  recordings: number
  gigs: number
  venues: number
  images: number
  videos: number
  total: number
}

type QuizStats = {
  total_answered: number
  total_correct: number
  percentage: number
}

type UserDetail = {
  id: number
  name: string
  email: string
  roles: string[]
  ad_free_lifetime:     boolean
  ad_free_until:        string | null
  donations_count:      number
  total_donated_cents:  number
  email_verified:        boolean
  email_verified_at:     string | null
  show_on_wall_of_fame:  boolean
  show_on_leaderboard:   boolean
  timezone:             string | null
  created_at:           string
  updated_at:           string
}

const MANAGEABLE_ROLES = ["admin", "editor", "contributor"] as const
type RoleName = typeof MANAGEABLE_ROLES[number]

function adFreeStatus(user: UserDetail): string {
  if (user.ad_free_lifetime) return "Lifetime (permanent)"
  if (user.ad_free_until) {
    const expiry = new Date(user.ad_free_until)
    const active = expiry > new Date()
    return active
      ? `Active until ${formatDate(user.ad_free_until)}`
      : `Expired ${formatDate(user.ad_free_until)}`
  }
  return "None"
}

function useAdminToggle(userId: number, path: string, initial: boolean) {
  const [value, setValue] = useState(initial)
  const toggle = async () => {
    const next = !value
    setValue(next)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
    const res  = await fetch(`/admin/users/${userId}/${path}`, {
      method:  "PATCH",
      headers: { "X-CSRF-Token": csrf, "Accept": "application/json" },
    })
    if (!res.ok) setValue(!next)
  }
  return [ value, toggle ] as const
}

// One-way: an admin vouching for an account. Not a toggle — there is no
// meaningful "un-verify", and the server refuses to overwrite a genuine
// user-performed verification anyway (#363).
function useVerifyEmail(userId: number, initiallyVerified: boolean) {
  const [verified, setVerified] = useState(initiallyVerified)
  const [saving, setSaving] = useState(false)

  const verify = async () => {
    setSaving(true)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
    const res = await fetch(`/admin/users/${userId}/verify_email`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrf, "Accept": "application/json" },
    })
    if (res.ok) setVerified(true)
    setSaving(false)
  }

  return { verified, saving, verify }
}

function useRoles(userId: number, initialRoles: string[]) {
  const [roles, setRoles] = useState<string[]>(initialRoles)
  const toggle = async (role: RoleName) => {
    const next = roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role]
    setRoles(next)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
    const res  = await fetch(`/admin/users/${userId}/toggle_role`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ role }),
    })
    if (!res.ok) setRoles(roles)
  }
  return [ roles, toggle ] as const
}

export default function AdminUserShow({ user, contributions, quiz_stats }: { user: UserDetail; contributions: Contributions; quiz_stats: QuizStats }) {
  const [wofVisible, toggleWof]            = useAdminToggle(user.id, "toggle_wall_of_fame", user.show_on_wall_of_fame)
  const [leaderVisible, toggleLeaderboard] = useAdminToggle(user.id, "toggle_leaderboard", user.show_on_leaderboard)
  const [roles, toggleRole]                = useRoles(user.id, user.roles)
  const emailVerification                  = useVerifyEmail(user.id, user.email_verified)

  function handleDelete() {
    const message = contributions.total > 0
      ? `This user has made ${contributions.total.toLocaleString()} contributions to the database. Are you sure you want to delete them?`
      : `Are you sure you want to delete ${user.name || user.email}?`
    if (confirm(message)) {
      router.delete(`/admin/users/${user.id}`)
    }
  }

  return (
    <>
      <Head title={user.email}>
        <meta name="description" content={`User detail for ${user.email}.`} />
        <meta property="og:title" content={user.email} />
        <meta property="og:description" content={`User detail for ${user.email}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{user.name || user.email}</h1>
              <p className="mt-1">User #{user.id}</p>
            </div>
            <div className="flex items-center gap-2">
              {user.ad_free_lifetime && <Badge tone="signal">Ad-free forever</Badge>}
              {roles.includes("admin") && <Badge tone="accent">Admin</Badge>}
              {roles.includes("editor") && <Badge tone="neutral">Editor</Badge>}
              {roles.includes("contributor") && <Badge tone="neutral">Contributor</Badge>}
              <Button variant="danger" onClick={handleDelete}>Delete</Button>
              <Link
                href="/admin/users"
                className="text-sm text-ink-muted no-underline hover:text-ink-display"
              >
                Back to users
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-8">
          <section>
            <h2 className="mb-4 text-base">Account</h2>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
              <DetailRow label="Name" value={user.name || "—"} />
              <DetailRow label="Email" value={user.email} />
              <DetailRow label="Timezone" value={user.timezone ?? "—"} />
              <DetailRow label="Created" value={formatDateTime(user.created_at)} />
              <DetailRow label="Updated" value={formatDateTime(user.updated_at)} />
            </dl>
          </section>

          <section>
            <h2 className="mb-4 text-base">Roles</h2>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
              {MANAGEABLE_ROLES.map((role) => (
                <React.Fragment key={role}>
                  <dt className="text-sm text-ink-muted self-center capitalize">{role}</dt>
                  <dd className="text-sm text-ink-body">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-amber-500"
                        checked={roles.includes(role)}
                        onChange={() => toggleRole(role)}
                      />
                      <span>{roles.includes(role) ? `Has ${role} access` : `No ${role} access`}</span>
                    </label>
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base">Donations &amp; ad-free access</h2>
              {user.donations_count > 0 && (
                <Link href={`/admin/donations?user_id=${user.id}`} className="text-sm text-ink-muted no-underline hover:text-ink-display">
                  View donations →
                </Link>
              )}
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
              <DetailRow label="Ad-free status" value={adFreeStatus(user)} />
              <DetailRow label="Donations" value={String(user.donations_count)} />
              <DetailRow
                label="Total donated"
                value={user.total_donated_cents > 0
                  ? `$${(user.total_donated_cents / 100).toFixed(2)} AUD`
                  : "—"}
              />
              <dt className="text-sm text-ink-muted self-center">Wall of Fame</dt>
              <dd className="text-sm text-ink-body">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-amber-500"
                    checked={wofVisible}
                    onChange={toggleWof}
                  />
                  <span>{wofVisible ? "Listed as contributor" : "Hidden from contributors"}</span>
                </label>
              </dd>
              <dt className="text-sm text-ink-muted self-center">Email</dt>
              <dd className="text-sm text-ink-body">
                {emailVerification.verified ? (
                  <span>
                    Verified
                    {user.email_verified_at && ` on ${formatDate(user.email_verified_at)}`}
                  </span>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-danger-display">Never verified</span>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={emailVerification.saving}
                      onClick={emailVerification.verify}
                    >
                      {emailVerification.saving ? "Saving…" : "Mark as verified"}
                    </Button>
                    <span className="text-xs text-ink-muted">
                      Use for imported accounts, or people you have confirmed another way.
                    </span>
                  </div>
                )}
              </dd>
              <dt className="text-sm text-ink-muted self-center">Leaderboard</dt>
              <dd className="text-sm text-ink-body">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-amber-500"
                    checked={leaderVisible}
                    onChange={toggleLeaderboard}
                  />
                  <span>{leaderVisible ? "Shown on leaderboard" : "Hidden from leaderboard"}</span>
                </label>
              </dd>
            </dl>
          </section>
          <section>
            <h2 className="mb-4 text-base">Contributions</h2>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
              <DetailRow label="Bands"      value={contributions.bands.toLocaleString()} />
              <DetailRow label="People"     value={contributions.people.toLocaleString()} />
              <DetailRow label="Recordings" value={contributions.recordings.toLocaleString()} />
              <DetailRow label="Gigs"       value={contributions.gigs.toLocaleString()} />
              <DetailRow label="Venues"     value={contributions.venues.toLocaleString()} />
              <DetailRow label="Images"     value={contributions.images.toLocaleString()} />
              <DetailRow label="Videos"     value={contributions.videos.toLocaleString()} />
              <dt className="text-sm text-ink-muted font-medium">Total</dt>
              <dd className="text-sm text-ink-body font-medium">{contributions.total.toLocaleString()}</dd>
            </dl>
          </section>

          <section>
            <h2 className="mb-4 text-base">Trivia</h2>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
              <DetailRow label="Questions answered" value={quiz_stats.total_answered.toLocaleString()} />
              <DetailRow label="Correct"            value={quiz_stats.total_correct.toLocaleString()} />
              <DetailRow
                label="Score"
                value={quiz_stats.total_answered > 0 ? `${quiz_stats.percentage}%` : "—"}
              />
            </dl>
          </section>
        </div>
      </AdminShell>
    </>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink-body">{value}</dd>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
